/* eslint-disable */
import apiClient from './api-client';

// GET /v1/onboarding/cities
//
// Returns the list the "Select your city or region" sheet shows, already
// converted to the shape the screen uses:
//   {id, name, region, stationsActive, latitude, longitude}
//
// The backend field names are read in a forgiving way (name / city / cityName,
// region / state / province, lat / latitude ...), and the list may be the
// "Data" itself or sit under Data.cities / Data.regions / Data.items. If the
// real response uses other names, only `pick*` and `adaptCity` below change.

const pickList = json => {
  const d = json?.Data ?? json?.data ?? json;
  if (Array.isArray(d)) {
    return d;
  }
  for (const key of ['cities', 'regions', 'items', 'list', 'results']) {
    if (Array.isArray(d?.[key])) {
      return d[key];
    }
  }
  return [];
};

const firstDefined = (...values) => values.find(v => v !== undefined && v !== null);

const toNumber = v => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

const slug = text =>
  String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// A-Z by city name. A leading apostrophe or symbol is ignored, so
// "'s-Gravenhage" is placed under G, not at the very top of the list.
// localeCompare with sensitivity 'base' also ignores capitals and accents.
const sortKey = name => String(name || '').replace(/^[^a-zA-Z0-9]+/, '');
export const byName = (a, b) =>
  sortKey(a.name).localeCompare(sortKey(b.name), 'en', {sensitivity: 'base'});

export const adaptCity = c => {
  const name = firstDefined(c?.name, c?.city, c?.cityName, c?.title, '');
  const postcode = firstDefined(c?.postcode, c?.postalCode, c?.zip, '');
  // The API has no id, so build one from the name + postcode.
  const id = firstDefined(
    c?.id,
    c?._id,
    c?.code,
    c?.slug,
    c?.placeId,
    slug(`${name}-${postcode}`),
  );
  return {
    id: String(id),
    name: String(name),
    postcode: String(postcode || ''),
    municipality: String(c?.municipality || ''),
    // region = province in English, regionNl = the same in Dutch
    region: String(
      firstDefined(c?.region, c?.state, c?.province, c?.country, '') || '',
    ),
    regionNl: String(c?.provinceNl || ''),
    stationsActive: toNumber(
      firstDefined(
        c?.stationsActive,
        c?.stations_active,
        c?.activeStations,
        c?.stationCount,
        c?.stations,
      ),
    ),
    latitude: toNumber(firstDefined(c?.latitude, c?.lat)),
    longitude: toNumber(firstDefined(c?.longitude, c?.lng, c?.long, c?.lon)),
  };
};

// The list is kept for the session, so reopening the sheet doesn't call the
// API again. While a request is running, everyone who asks gets that same
// request (so a prefetch and the sheet never call the API twice).
// A failed call is never kept: "Try again" always asks the server.
let cache = null;
let inflight = null;

// Only the popular cities: a short list, so it loads fast.
// Typing a name searches beyond this list (see CitySearchSheet).
const CITIES_PATH = 'onboarding/cities?popular=true';

// The list from an earlier call, or null, so the sheet can open without a
// loading state.
export const getCachedCities = () => cache;

export const getOnboardingCities = ({force = false, language = 'en'} = {}) => {
  if (cache && !force) {
    return Promise.resolve(cache);
  }
  if (inflight && !force) {
    return inflight;
  }
  inflight = apiClient
    .get(CITIES_PATH, {
      timeout: 8000,
      headers: {'Accept-Language': language},
    })
    .then(res => {
      const json = res.data;

      // Some endpoints answer HTTP 200 but put a failure flag in the body.
      if (json?.Success === 0 || json?.success === false || json?.status === false) {
        throw new Error(json?.Message || json?.message || 'Failed to load cities');
      }

      // Make sure every id is unique (the list is used as React keys).
      const seen = new Set();
      const list = pickList(json)
        .map(adaptCity)
        .filter(c => c.name)
        .sort(byName)
        .map((c, i) => {
          const id = seen.has(c.id) ? `${c.id}-${i}` : c.id;
          seen.add(id);
          return {...c, id};
        });
      cache = list;
      return list;
    })
    .catch(error => {
      throw new Error(
        error.response?.data?.Message ||
          error.response?.data?.message ||
          error.message ||
          'Failed to load cities',
      );
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

// Start loading in the background and ignore the result, so the list is
// already there when the user opens the city sheet.
export const prefetchOnboardingCities = language => {
  getOnboardingCities({language}).catch(() => {});
};